import { Page, Pagebar, PagebarTitle } from '@monorepo/base/src/components/page/page';
import { FC, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Outlet } from 'react-router-dom';
import { useDomainFactorsGet } from '../../modules/hooks/apis/use-domain-factors-get';
import { ButtonGroup, GButton } from '@monorepo/base/src/components/buttons/group/button-group';
import { Input } from '@monorepo/base/src/components/form/input/input';
import { useDebounce } from '@monorepo/tools/src/lib/hooks/utils/use-debounce';
import { Dropdown } from '@monorepo/base/src/components/dropdown/dropdown';
import { CampaignsApi } from '../../modules/apis/campaigns.api';
import { useRoute } from '@monorepo/tools/src/lib/hooks/tools/use-route';
import { useDomainFactorsHeaders } from '../../modules/hooks/theaders/domain-factors.headers';
import { ControlledTable } from '@monorepo/controlled/src/components/table-wrapper/table-wrapper';
import styles from './domain-factors.module.scss';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { IAdminxStore } from '../../modules/stores';
import { useStateBetweenPages } from '../../modules/hooks/tools/use-state-between-pages';
import routes from '../../modules/routes/index.routes';
import { useDomainFactorsActions } from '../../modules/hooks/actions/domain-factors.actions';

export const DomainFactorsPage: FC = observer(() => {
	const { currentRouteWithoutSpecialChars } = useRoute();
	const { columns } = useDomainFactorsHeaders();
	const [domainsToFetch, setDomainsToFetch] = useState<string>('all');
	const [campaignId, setCampaignId] = useState<string>('');
	const [domain, setDomain] = useState<string>('');
	const [query, setQuery] = useState<string>('');
	const [campaignOptions, setCampaignOptions] = useState<string[]>([]);
	const [isCampaignsLoading, setIsCampaignsLoading] = useState(false);
	const { SelectedActions } = useDomainFactorsActions();

	useEffect(() => {
		let cancelled = false;
		setIsCampaignsLoading(true);
		CampaignsApi.list()
			.then(campaigns => {
				if (!cancelled) {
					setCampaignOptions(campaigns?.map(c => `${c.id} - ${c.name}`) ?? []);
				}
			})
			.finally(() => {
				if (!cancelled) {
					setIsCampaignsLoading(false);
				}
			});
		return () => {
			cancelled = true;
		};
	}, []);

	const rawQuery = domainsToFetch === 'campaign' ? campaignId : domainsToFetch === 'domain' ? domain : undefined;
	const debouncedQuery = useDebounce(rawQuery, 800);
	const { domainFactorsStore } = useStores<IAdminxStore>();

	useEffect(() => {
		if (debouncedQuery) {
			setQuery(debouncedQuery);
		} else {
			setQuery('');
		}
	}, [debouncedQuery]);

	const stateBetweenPages = useStateBetweenPages(() => domainFactorsStore.listStore.getList().length !== 0);
	const { error, isLoading, domainFactors } = useDomainFactorsGet(
		domainsToFetch as 'all' | 'campaign' | 'domain',
		query,
		stateBetweenPages
	);
	const selectionKey = `${domainsToFetch}|${query}|${domainFactors.map(f => f.getId()).join(',')}`;

	function parseCampaignIdFromLabel(label: string): string {
		const idPrefix = label.match(/^(\d+)\s*-\s*/);
		return idPrefix?.[1] ?? '';
	}

	const createQuery = (() => {
		if (domainsToFetch === 'campaign' && campaignId) {
			return `?campaign_id=${campaignId}`;
		}
		if (domainsToFetch === 'domain' && domain) {
			return `?domain=${encodeURIComponent(domain)}`;
		}
		return '';
	})();

	return (
		<Page>
			<Pagebar>
				<PagebarTitle>Domain Factors</PagebarTitle>
			</Pagebar>

			<ButtonGroup
				defaultValue="all"
				onChange={values => {
					const next = values.values().next().value ?? 'all';
					setDomainsToFetch(next);
					setCampaignId('');
					setDomain('');
					setQuery('');
					domainFactorsStore.listStore.reset();
				}}>
				<GButton text="All" value="all" />
				<GButton text="Campaign" value="campaign" />
				<GButton text="Domain" value="domain" />
			</ButtonGroup>
			{domainsToFetch === 'campaign' ? (
				<Dropdown
					label="Campaign"
					className={styles.campaignDropdown}
					wrapperClassName={styles.campaignDropdownWrapper}
					placeholder="Select campaign"
					autocomplete
					autocompletePlaceholder="Search by name or campaign ID"
					isLoading={isCampaignsLoading}
					disabled={isCampaignsLoading || campaignOptions.length === 0}
					options={campaignOptions}
					defaultOption={campaignOptions.find(o => o.startsWith(`${campaignId} -`))}
					onSelect={val => {
						const label = val?.values().next().value;
						setCampaignId(label ? parseCampaignIdFromLabel(label) : '');
					}}
					isCloseIcon={true}
				/>
			) : null}
			{domainsToFetch === 'domain' ? <Input topLabel="Domain" value={domain} onValue={v => setDomain(v)} /> : null}

			<div className={styles.tableWrapper}>
				<ControlledTable
					key={selectionKey}
					currentRoute={currentRouteWithoutSpecialChars}
					columns={columns}
					data={domainFactors}
					isLoading={isLoading}
					isError={!!error}
					createRoute={`${routes.domainFactors.create()}${createQuery}`}>
					{() => ({
						SelectedActions,
						manualSorting: false,
					})}
				</ControlledTable>
			</div>
			<Outlet />
		</Page>
	);
});
DomainFactorsPage.displayName = 'DomainFactorsPage';
